import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicHeader } from '@/components/public/PublicHeader';
import { PublicFooter } from '@/components/public/PublicFooter';
import { useAuth } from '@/contexts/AuthContext';

interface GlobalLayoutProps {
  children?: React.ReactNode;
}

const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  
  // Safely get user with error handling
  let user = null;
  try {
    const authContext = useAuth();
    user = authContext.user;
  } catch (error) {
    // If AuthProvider is not available yet, user remains null
    console.warn('AuthProvider not yet initialized');
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Define routes that should NOT show any headers (like standalone auth pages)
  const noHeaderRoutes = [
    '/auth/login',
    '/auth/register', 
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/email-verification'
  ];

  // Check if current route is a print route (should have no header/footer)
  const isPrintRoute = location.pathname.startsWith('/qr-flyer/print/');

  // Define dashboard routes that should NOT show the footer
  const dashboardRoutes = [
    '/dashboard',
    '/club-dash',
    '/athlete-dashboard',
    '/college-coach-dashboard',
    '/club-coach-dashboard',
    '/parent-dashboard',
    '/admin-dashboard',
    '/admin/',
    '/sales-dashboard',
    '/staff-dashboard',
    '/scout-dashboard',
    '/press-dashboard',
    '/video-press-dashboard',
    '/vendor-video-dashboard',
    '/vendor-graphics-dashboard',
    '/vendor-press-dashboard',
    '/affiliate-dashboard',
    '/editor-dashboard'
  ];

  const isDashboardPage = dashboardRoutes.some(route => location.pathname.startsWith(route));
  // Dashboards have their own headers/sidebars — never show the public header on them
  const shouldShowHeaders = !noHeaderRoutes.includes(location.pathname) && !isPrintRoute && !isDashboardPage;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {shouldShowHeaders && (
        <PublicHeader />
      )}
      
      <div className={`flex-1 ${shouldShowHeaders ? 'pt-[120px]' : ''}`}>
        {children || <Outlet />}
      </div>
      
      {shouldShowHeaders && !isDashboardPage && (
        <PublicFooter />
      )}
    </div>
  );
};

export default GlobalLayout;